/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {catchError, from, map, Observable, tap, throwError} from 'rxjs';

import {getAuthHttpBuffer} from '../../../components/auth/auth-http-buffer.service';
import {AuthHttpInterceptorService} from '../../../components/auth/auth-http-interceptor.service';
import {
  AdnHttpProgressCallback,
  AdnHttpRequest,
  AdnHttpResponse,
  AdnHttpService
} from '../../../components/util/adn-http.service';
import {ApiErrorHandlerService} from '../directives/error/api-error-handler.service';
import {ApiConfig} from '../../config/api-config';

/** Typed Observable transport with the application's auth and API error behaviour. */
@Injectable({providedIn: 'root'})
export class AdnApiClientService {
  constructor(
    private readonly http: AdnHttpService,
    private readonly auth: AuthHttpInterceptorService,
    private readonly apiErrors: ApiErrorHandlerService,
    readonly apiConfig: ApiConfig
  ) {
  }

  request<T>(config: AdnHttpRequest): Observable<T> {
    return this.requestResponse(config).pipe(
      map((response) => response.data as T)
    );
  }

  requestResponse(config: AdnHttpRequest): Observable<AdnHttpResponse> {
    const request = this.auth.prepare({
      ...config,
      params: {...(config.params || {})},
      headers: {...(config.headers || {})}
    }) as AdnHttpRequest;

    return this.http.request$(request).pipe(
      tap((response) => this.apiErrors.handleSuccess(response)),
      catchError((response: any) => this.handleFailure(request, response))
    );
  }

  upload<T>(config: AdnHttpRequest, onProgress?: AdnHttpProgressCallback): Observable<T> {
    const request = this.auth.prepare({
      ...config,
      params: {...(config.params || {})},
      headers: {...(config.headers || {})}
    }) as AdnHttpRequest;

    return this.http.upload$(request, onProgress).pipe(
      tap((response) => this.apiErrors.handleSuccess(response)),
      catchError((response: any) => this.handleFailure(request, response)),
      map((response) => response.data as T)
    );
  }

  private handleFailure(request: AdnHttpRequest, response: any): Observable<AdnHttpResponse> {
    if (response?.xhrStatus === 'abort') {
      return throwError(() => response);
    }
    const authBuffer = getAuthHttpBuffer();
    if (response?.status === 401 && authBuffer && !request.ignoreAuthModule) {
      return from(authBuffer.bufferRequest(request, response)).pipe(
        tap((retried) => this.apiErrors.handleSuccess(retried))
      );
    }
    return throwError(() => this.apiErrors.handleError(response));
  }
}
